import { awaitDataServiceFetcher } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/DataServiceFetchers';
import { SuccessCallback } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/DataService/types';
import { normalizeEmptyResponseToSuccessResponse } from '@dxb-web-ui-framework/common/domain/RestfulDataTransfer/infrastructure';
import { AxiosResponseHeaders } from 'axios';
import {
  CreatedFavouriteTransaction,
  FavouriteTransaction,
  FavouriteTransactionEnabledStatus,
  NewFavouriteTransactionRequestDTO,
} from '../entities/FavouriteTransaction';
import { ITransactionRepository } from '../entities/ITransactionRepository';
import { ConsolidatedTransactionDetails } from '../entities/ConsolidatedTransaction';
import { TransactionStatus } from '../entities/TransactionStatus';
import { TransactionType } from '../entities/TransactionType';
import { AccountExtraitTransactions } from '../entities/AccountExtraitTransaction/AccountExtraitTransactionResponseDTO';
import { OrderStatus } from '../entities/OrderStatus';
import { TransactionDocument, TransactionDocumentData } from '../entities/TransactionDocuments';
import { ConsolidatedTransactions } from '../entities/ConsolidatedTransaction/ConsolidatedTransactionResponseDTO';
import { TransactionsWithDealerRateServiceBody } from '../entities/TransactionWithDealerRate/TransactionWithDealerRateResponseDTO';
import { AccountExtraitTransactionsPrint } from '../entities/AccountExtraitTransaction/AccountExtraitTransactionsPrintResponseDTO';
import { ConsolidatedTransactionsPrint } from '../entities/ConsolidatedTransaction/ConsolidatedTransactionsPrintResponseDTO';
import { ConsolidatedTransactionDetailsPrint } from '../entities/ConsolidatedTransaction/ConsolidatedTransactionDetailsPrintResponseDTO';
import { TransactionDocumentDeleteData } from '../entities/TransactionDocuments/TransactionDocumentDeleteResponseDTO';
import { TransactionStatusResponseDTOtoTransactionStatusAdapter } from './TransactionStatus';
import {
  AccountExtraitTransactionResponseDTOtoTransactionAdapter,
  AccountExtraitTransactionsPrintResponseDTOtoTransactionPrintAdapter,
} from './AccountExtraitTransaction';
import { TransactionTypeResponseDTOtoTransactionTypeAdapter } from './TransactionType';
import { OrderStatusResponseDTOtoOrderStatusAdapter } from './OrderStatus';
import {
  createdFavouriteTransactionsResponseDTOToCreatedFavouriteTransactionsAdapter,
  favouriteTransactionDTOToFavouriteTransactionListAdapter,
} from './FavouriteTransaction';
import {
  ConsolidatedTransactionDetailsPrintResponseDTOtoConsolidatedTransactionDetailsPrintAdapter,
  ConsolidatedTransactionDetailsResponseDTOtoConsolidatedTransactionDetailsAdapter,
  ConsolidatedTransactionsResponseDTOtoConsolidatedTransactionsAdapter,
  ConsolidatedTransactionsPrintResponseDTOtoTransactionPrintAdapter,
} from './ConsolidatedTransaction';
import { favouriteTransactionToEditFavouriteTransactionRequestDTOAdapter } from './FavouriteTransaction';
import {
  transactionDocumentUploadResponseDTOtoTransactionDocumentUploadAdapter,
  TransactionDocumentsResponseDTOtoTransactionDocumentsAdapter,
} from './TransactionDocuments';
import { TransactionWithDealerRateResponseDTOtoTransactionsWithDealerRateResponseAdapter } from './TransactionWithDealerRate';

class TransactionRepositoryImpl implements ITransactionRepository {
  constructor() {
    this.GetAccountExtraitTransactions = this.GetAccountExtraitTransactions.bind(this);
    this.GetConsolidatedTransactions = this.GetConsolidatedTransactions.bind(this);
    this.GetFavouriteTransactions = this.GetFavouriteTransactions.bind(this);
    this.CreateNewFavouriteTransaction = this.CreateNewFavouriteTransaction.bind(this);
    this.EditFavouriteTransaction = this.EditFavouriteTransaction.bind(this);
    this.DeleteFavouriteTransaction = this.DeleteFavouriteTransaction.bind(this);
    this.GetTransactionStatuses = this.GetTransactionStatuses.bind(this);
    this.GetTransactionTypes = this.GetTransactionTypes.bind(this);
    this.GetOrderStatuses = this.GetOrderStatuses.bind(this);
    this.GetConsolidatedTransactionDetails = this.GetConsolidatedTransactionDetails.bind(this);
    this.GetTransactionDocuments = this.GetTransactionDocuments.bind(this);
    this.GetTransactionDocumentData = this.GetTransactionDocumentData.bind(this);
    this.GetAccountTransactionsPrint = this.GetAccountTransactionsPrint.bind(this);
    this.GetConsolidatedTransactionsPrint = this.GetConsolidatedTransactionsPrint.bind(this);
    this.GetConsolidatedTransactionDetailsPrint = this.GetConsolidatedTransactionDetailsPrint.bind(this);
    this.GetSwiftConfirmationPrint = this.GetSwiftConfirmationPrint.bind(this);
    this.UploadDocument = this.UploadDocument.bind(this);
    this.DeleteDocument = this.DeleteDocument.bind(this);
    this.GetTransactionsWithDealerRate = this.GetTransactionsWithDealerRate.bind(this);
  }

  GetAccountExtraitTransactions(
    accountNumber: string,
    pageSize: number,
    pageOffset: number,
    allEntries?: string,
    dateFrom?: string,
    dateTo?: string,
    fromAmount?: number,
    toAmount?: number,
    transactionType?: number,
  ): Promise<AccountExtraitTransactions> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/secure-web/api/private/transactions/account/${accountNumber}`,
        dataAdapter: AccountExtraitTransactionResponseDTOtoTransactionAdapter,
        params: {
          pageLimit: pageSize,
          pageOffset,
          allEntries,
          dateFrom,
          dateTo,
          amountFrom: fromAmount,
          amountTo: toAmount,
          transactionType,
        },
      }),
    });
  }

  GetConsolidatedTransactions(
    beneficiaryName: string,
    fromAmount: number,
    toAmount: number,
    fromTrxDate: string,
    toTrxDate: string,
    pageSize: number,
    pageOffset: number,
    customerId: number,
    valueDateFrom?: string,
    valueDateTo?: string,
    orderBeneficiaryAccount?: string,
    orderStatus?: number,
    accountNumber?: string,
    orderDirection?: number,
  ): Promise<ConsolidatedTransactions> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: '/secure-web/api/private/transactions/fund-transfers/consolidated-history',
        dataAdapter: ConsolidatedTransactionsResponseDTOtoConsolidatedTransactionsAdapter,
        params: Object.assign(
          {
            beneficiaryName,
            fromOrderAmountFilter: fromAmount,
            toOrderAmountFilter: toAmount,
            fromTrxDate,
            toTrxDate,
            paginationLimit: pageSize,
            paginationOffset: pageOffset,
            customerId,
            orderBeneficiaryAccount,
            orderStatuses: typeof orderStatus === 'number' ? [orderStatus] : [],
            accountNumber,
            orderDirection,
          },
          valueDateFrom && { valueDateFrom },
          valueDateTo && { valueDateTo },
        ),
      }),
    });
  }

  GetConsolidatedTransactionDetails(
    orderId: number | string,
    accountNumber: string,
    successCallback?: SuccessCallback,
  ): Promise<ConsolidatedTransactionDetails> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: '/secure-web/api/private/transactions/fund-transfers/single',
        dataAdapter: ConsolidatedTransactionDetailsResponseDTOtoConsolidatedTransactionDetailsAdapter,
        params: { orderId, accountNumber },
        nextCallback: successCallback,
        headers: {
          Accept: 'application/vnd.intrasoft.v2+json',
        },
      }),
    });
  }

  GetFavouriteTransactions(): Promise<FavouriteTransaction[]> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: '/secure-web/api/private/users/transactions/favorites',
        dataAdapter: favouriteTransactionDTOToFavouriteTransactionListAdapter,
        params: {
          enabledStatus: FavouriteTransactionEnabledStatus.ENABLED,
        },
      }),
    });
  }

  CreateNewFavouriteTransaction(
    appTransactionId: number,
    requestData: string,
    description: string,
    transactionStatus: number,
    enabled: boolean,
  ): Promise<CreatedFavouriteTransaction> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'POST',
        url: '/secure-web/api/private/users/transactions/favorites',
        dataAdapter: createdFavouriteTransactionsResponseDTOToCreatedFavouriteTransactionsAdapter,
        data: new NewFavouriteTransactionRequestDTO(
          appTransactionId,
          requestData,
          description,
          transactionStatus,
          enabled,
        ),
      },
    });
  }

  EditFavouriteTransaction(
    data: FavouriteTransaction,
    successCallback: SuccessCallback,
  ): Promise<AxiosResponseHeaders> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'PUT',
        url: `/secure-web/api/private/users/transactions/favorites/${data.appTransactionUniqueId}`,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        data: favouriteTransactionToEditFavouriteTransactionRequestDTOAdapter,
        nextCallback: successCallback,
      },
    });
  }

  DeleteFavouriteTransaction(
    appTransactionUniqueId: number,
    successCallback: SuccessCallback,
  ): Promise<AxiosResponseHeaders> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'DELETE',
        url: `/secure-web/api/private/users/transactions/favorites/${appTransactionUniqueId}`,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        nextCallback: successCallback,
      },
    });
  }

  GetTransactionStatuses(): Promise<TransactionStatus[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/api/private/transactions/parameters/statuses',
        dataAdapter: TransactionStatusResponseDTOtoTransactionStatusAdapter,
      },
    });
  }

  GetTransactionTypes(): Promise<TransactionType[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/api/private/transactions/parameters/types',
        dataAdapter: TransactionTypeResponseDTOtoTransactionTypeAdapter,
      },
    });
  }

  GetOrderStatuses(): Promise<OrderStatus[]> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: '/secure-web/api/private/transfers/parameters/order-statuses',
        dataAdapter: OrderStatusResponseDTOtoOrderStatusAdapter,
      },
    });
  }

  GetTransactionDocuments(customerId: number, orderId: number | string): Promise<TransactionDocument[]> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/secure-web/api/private/transactions/document/${customerId}/${orderId}`,
        dataAdapter: TransactionDocumentsResponseDTOtoTransactionDocumentsAdapter,
      }),
    });
  }

  GetTransactionDocumentData(
    customerId: number,
    fileSn: number,
    referenceNumber: string,
  ): Promise<TransactionDocumentData> {
    return awaitDataServiceFetcher({
      payload: {
        method: 'GET',
        url: `/secure-web/api/private/transactions/document/${customerId}/data`,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        params: {
          fileSn,
          referenceNumber,
        },
      },
    });
  }

  GetAccountTransactionsPrint(
    accountNumber: string,
    type: string,
    allEntries?: string,
    dateFrom?: string,
    dateTo?: string,
    fromAmount?: number,
    toAmount?: number,
    transactionType?: number,
  ): Promise<AccountExtraitTransactionsPrint> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/secure-web/api/private/transactions/account/${accountNumber}/print/${type}`,
        params: {
          allEntries,
          dateFrom,
          dateTo,
          amountFrom: fromAmount,
          amountTo: toAmount,
          transactionType,
        },
        dataAdapter: AccountExtraitTransactionsPrintResponseDTOtoTransactionPrintAdapter,
      }),
    });
  }

  GetConsolidatedTransactionsPrint(
    type: string,
    fromAmount: number,
    toAmount: number,
    fromTrxDate: string,
    toTrxDate: string,
    customerId: number,
    valueDateFrom?: string,
    valueDateTo?: string,
    beneficiaryName?: string,
    orderStatus?: number,
    accountNumber?: string,
    orderDirection?: number,
  ): Promise<ConsolidatedTransactionsPrint> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/secure-web/api/private/transactions/fund-transfers/consolidated-history/print/${type}`,
        params: Object.assign(
          {
            fromOrderAmountFilter: fromAmount,
            toOrderAmountFilter: toAmount,
            fromTrxDate,
            toTrxDate,
            orderStatuses: typeof orderStatus === 'number' ? [orderStatus] : [],
            accountNumber,
            beneficiaryName,
            customerId,
            orderDirection,
          },
          valueDateFrom && { valueDateFrom },
          valueDateTo && { valueDateTo },
        ),
        dataAdapter: ConsolidatedTransactionsPrintResponseDTOtoTransactionPrintAdapter,
      }),
    });
  }

  GetConsolidatedTransactionDetailsPrint(
    orderId: number,
    accountNumber: string,
  ): Promise<ConsolidatedTransactionDetailsPrint> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/secure-web/api/private/transactions/fund-transfers/single/PDF`,
        params: {
          orderId,
          accountNumber,
        },
        dataAdapter: ConsolidatedTransactionDetailsPrintResponseDTOtoConsolidatedTransactionDetailsPrintAdapter,
      }),
    });
  }

  GetSwiftConfirmationPrint(orderId: number, accountNumber: string): Promise<ConsolidatedTransactionDetailsPrint> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/secure-web/api/private/transactions/fund-transfers/swift-confirmation/print/PDF`,
        params: {
          orderId,
          accountNumber,
        },
        dataAdapter: ConsolidatedTransactionDetailsPrintResponseDTOtoConsolidatedTransactionDetailsPrintAdapter,
      }),
    });
  }

  UploadDocument(
    customerId: number,
    fileData: string,
    fileName: string,
    accountNumber: string,
    orderCode: number,
    successCallback: SuccessCallback,
  ): Promise<TransactionDocumentData> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'POST',
        url: `/api/private/transactions/document/${customerId}`,
        data: {
          documentData: fileData,
          pathName: fileName,
          accountNumber,
          orderCode,
        },
        dataAdapter: transactionDocumentUploadResponseDTOtoTransactionDocumentUploadAdapter,
        nextCallback: successCallback,
      }),
    });
  }

  DeleteDocument(
    customerId: number,
    fileSn: number,
    referenceNumber: string,
    successCallback: SuccessCallback,
  ): Promise<TransactionDocumentDeleteData> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'DELETE',
        url: `/api/private/transactions/document/${customerId}`,
        dataAdapter: normalizeEmptyResponseToSuccessResponse,
        data: {
          fileSn,
          referenceNumber,
        },
        nextCallback: successCallback,
      }),
    });
  }

  GetTransactionsWithDealerRate(
    customerId: string,
    fromOrderAmountFilter: number,
    toOrderAmountFilter: number,
    fromTrxDate: string,
    toTrxDate: string,
    successCallback?: SuccessCallback,
  ): Promise<TransactionsWithDealerRateServiceBody> {
    return awaitDataServiceFetcher({
      payload: Object.assign({
        method: 'GET',
        url: `/api/private/transactions/fund-transfers/dealer-rate/${customerId}`,
        dataAdapter: TransactionWithDealerRateResponseDTOtoTransactionsWithDealerRateResponseAdapter,
        params: {
          fromOrderAmountFilter,
          toOrderAmountFilter,
          fromTrxDate,
          toTrxDate,
        },
        nextCallback: successCallback,
      }),
    });
  }
}

export { TransactionRepositoryImpl };
